import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { UserActionsTab } from './UserActionsTab';
import { AuditStatistics, auditService } from '../../services/auditService';

interface AuditLogsModalProps {
  open: boolean;
  onClose: () => void;
}

export const AuditLogsModal: React.FC<AuditLogsModalProps> = ({ open, onClose }) => {
  const [statistics, setStatistics] = useState<AuditStatistics | null>(null);
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (open) {
      loadStatistics();
    }
  }, [open]);

  const loadStatistics = async () => {
    try {
      setLoading(true);
      const stats = await auditService.getStatistics();
      setStatistics(stats);
    } catch (error) {
      console.error('Error loading audit statistics:', error);
    } finally {
      setLoading(false);
    }
  };


  const formatNumber = (num: number): string => {
    return num.toLocaleString('en-US');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : '15px',
          minHeight: '80vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          backgroundColor: theme.palette.primary.main,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssessmentIcon />
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            User Activity Logs
          </Typography>
          {statistics && !loading && (
            <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
              <Chip 
                label={`Today: ${formatNumber(statistics.todayUserActions)}`}
                size="small"
                sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
              />
              <Chip 
                label={`Total: ${formatNumber(statistics.totalUserActions)}`}
                size="small"
                sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
              />
            </Box>
          )}
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
          <UserActionsTab />
        </Box>
      </DialogContent>
    </Dialog>
  );
};